import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, AlertCircle } from 'lucide-react';
import { createTicket } from '../utils/api';
import './ReportIssueModal.css';

const CATEGORIES = [
  { value: 'wrong_data',  label: 'Wrong / Unexpected Data' },
  { value: 'ui_bug',      label: 'UI / Display Issue' },
  { value: 'performance', label: 'Slow Performance' },
  { value: 'access',      label: 'Access / Login Issue' },
  { value: 'feature',     label: 'Feature Request' },
  { value: 'other',       label: 'Other' },
];

export default function ReportIssueModal({ onClose, conversationId }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({ title: '', description: '', category: 'wrong_data' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim()) {
      setError('Please fill in all fields.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const ticket = await createTicket({
        title:           form.title.trim(),
        description:     form.description.trim(),
        category:        form.category,
        conversation_id: conversationId || undefined,
      });
      onClose();
      navigate(`/support/${ticket.ticket_ref}`);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rim-overlay" onClick={onClose}>
      <div className="rim-modal" onClick={(e) => e.stopPropagation()}>
        <div className="rim-header">
          <h2 className="rim-title">Report an Issue</h2>
          <button className="rim-close" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="rim-form">
          <label className="rim-label">
            Category
            <select
              className="rim-select"
              value={form.category}
              onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
            >
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </label>

          <label className="rim-label">
            Short description
            <input
              className="rim-input"
              type="text"
              placeholder="e.g. Result showing wrong vendor name"
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              maxLength={200}
              autoFocus
            />
          </label>

          <label className="rim-label">
            Full details
            <textarea
              className="rim-textarea"
              rows={4}
              placeholder="What did you expect vs. what did you see? Include the SAP question you asked."
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              maxLength={5000}
            />
          </label>

          {conversationId && (
            <p className="rim-hint">This conversation will be linked to the ticket.</p>
          )}

          {error && (
            <div className="rim-error">
              <AlertCircle size={13} /> {error}
            </div>
          )}

          <div className="rim-actions">
            <button type="button" className="rim-btn rim-btn--ghost" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="rim-btn rim-btn--primary" disabled={submitting}>
              {submitting ? 'Submitting…' : 'Submit Ticket'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
